import { HTMLProps, useEffect, useMemo, useRef, useState } from "react";

import { DateTime } from "luxon";
import { useForm } from "react-hook-form";
import { DateRangeType } from "react-tailwindcss-datepicker";

import { useDebounce } from "@uidotdev/usehooks";
import { useQueryClient } from "@tanstack/react-query";
import { createColumnHelper, RowModel } from "@tanstack/react-table";
import { ChevronRightIcon, XMarkIcon } from "@heroicons/react/24/outline";

import PartImage from "./PartImage";
import Modal from "../../layouts/Modal";
import PopOutBox from "../shared/PopOutBox";
import useBaskets from "../../data/useBaskets";
import classNames from "../../utils/classNames";
import ClickToCopy from "../shared/ClickToCopy";
import { PageMode } from "../../models/PagedTable";
import openInNewTab from "../../utils/openInNewTab";
import OrderStatus from "../ebayOrders/OrderStatus";
import PagedTable from "../shared/Tables/PagedTable";
import { MakeCombobox } from "../parts/MakeCombobox";
import { IPart, IPartCategory, PartStatus } from "../../models/Part";
import PartStatusDropdown from "./PartStatusDropdown";
import ContactFromId from "../contacts/ContactFromId";
import SelectInput from "../shared/Inputs/SelectInput";
import { TextInput } from "../shared/Inputs/TextInput";
import { ModelCombobox } from "../parts/ModelCombobox";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import usePartMutations from "../../data/usePartMutations";
import { ListingStatusEnum } from "../../models/EbayOffer";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import useNotification from "../notifications/useNotifications";
import { useLabelGenerator } from "../../hooks/useLabelGenerator";
import useVehiclesMutations from "../../data/useVehiclesMutations";
import { ListingStatus } from "../../pages/EbayListings/ViewListing";
import PartCategoryCombo from "../leads/LeadParts/PartCategoryCombo";
import ChangeCategoryModal from "./stockFunctions/ChangeCatgeoryModal";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import UnlistedNpmUpdate from "../ebayListings/unlistedTable/UnlistedMpnUpdate";
import UnlistedOfferPrice from "../ebayListings/unlistedTable/UnlistedOfferPrice";
import WarehouseLocationFromId from "../warehouseLocations/WarehouseLocationFromId";
import WarehouseLocationSearch from "../warehouseLocations/WarehouseLocationSearch";
import useWarehouseLocations from "../../data/useWarehouseLocations";
import ComboBoxInput from "../shared/Inputs/ComboBoxInput";
import { IWarehouseLocation } from "../../models/Location";
import DateRangeFilter from "../shared/DateRangeFilter";
import useCategories from "../../data/useCategories";
import TableCheckboxCol from "../shared/Tables/IndeterminateCheckbox";

const colHelper = createColumnHelper<IPart>();


type LocationSearchMode = "equals" | "beginsWith";

interface IFormFields {
  make: string;
  model: string;
  year: string;
  bodyStyle: string;
  numberOfDoors: string;
  fuelType: string;
  transmissionType: string;
  engineSize: string;
  engineCode: string;
  yearFrom: string;
  yearTo: string;
  partNumber: string;
  partCategory: IPartCategory | undefined;
  vrm: string;
  tagNumber: string;
  smartSearch: string;
  dvlaYearOfManufacture: string;
  visibilityDate: string;
  terminateDate: string;
  listingStatus: ListingStatusEnum;
  locationSearchMode: LocationSearchMode;
  warehouseLocation: IWarehouseLocation | undefined;
  warehouseLocationId: string | undefined;
  warehouseLocationBeginsWith: string;
  dateFieldType: string | undefined;
};

const defaultFormValues: IFormFields = {
  make: "",
  model: "",
  year: "",
  bodyStyle: "",
  numberOfDoors: "",
  fuelType: "",
  transmissionType: "",
  engineSize: "",
  engineCode: "",
  yearFrom: "",
  yearTo: "",
  partNumber: "",
  partCategory: undefined,
  vrm: "",
  smartSearch: "",
  dvlaYearOfManufacture: "",
  visibilityDate: "",
  terminateDate: "",
  tagNumber: "",
  listingStatus: ListingStatusEnum["All"],
  locationSearchMode: "equals",
  warehouseLocation: undefined,
  warehouseLocationId: undefined,
  warehouseLocationBeginsWith: "",
  dateFieldType: "Created"
};

interface IStockTableProps {
  showActions?: boolean;
  showStatus?: boolean;

  advancedKey?: string;
  advancedUrl?: string;

  genericKey?: string;
  genericUrl?: string;

  pageMode?: PageMode;
}

// key={isAdvancedSearch ? "advanced-search" : "generic-search"}
// url={isAdvancedSearch ? "/part/stock" : "/part/listings"}

export default function StockTable({
  showActions = true,
  showStatus = true,
  advancedKey = "advanced-search",
  advancedUrl = "/part/stock",
  genericKey = "generic-search",
  genericUrl = "/part/listings",
  pageMode = PageMode.Stock,
}: IStockTableProps) {
  const { addNotification } = useNotification();
  const { addItemsMultiple } = useBaskets("StockTable");
  const { vrmLookup } = useVehiclesMutations();
  const { patchPart, bulkPatchPart, deletePart, bulkUpdateOfferPrice } = usePartMutations();
  const { warehouseLocations } = useWarehouseLocations();
  const { categories: allCategories } = useCategories();
  const { generatePartLabel } = useLabelGenerator();
  const { saveHandlers, deleteHandlers } = useDefaultCRUDHandlers("Part");
  const queryClient = useQueryClient();
  const { register, setValue, getValues, watch, reset, handleSubmit, formState } = useForm<IFormFields>({
    defaultValues: defaultFormValues,
  });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDelete, setToDelete] = useState<RowModel<IPart> | undefined>(undefined);
  const [toUpdate, setToUpdate] = useState<RowModel<IPart> | undefined>(undefined);
  const [statusUpdate, setStatusUpdate] = useState<number>(0);
  const [priceUpdate, setPriceUpdate] = useState<number>(0);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [isAdvancedSearch, setIsAdvancedSearch] = useState(false);
  const [isSmartMatch, setIsSmartMatch] = useState(false);
  const [isExtended, setIsExtended] = useState(false);
  const [updateStatusOpen, setUpdateStatusOpen] = useState(false);
  const [updatePriceOpen, setUpdatePriceOpen] = useState(false);
  const [updateCategoryOpen, setUpdateCategoryOpen] = useState(false);
  const [additionalBody, setAdditionalBody] = useState<Record<string, any>>({});
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined);
  const [isSearching, setIsSearching] = useState(false);
  const [dates, setDates] = useState<DateRangeType>({
    startDate: null,
    endDate: null
  });
  const [dateFieldType, setDateFieldType] = useState<string>("Created");

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const vehicleTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debounce the additionalBody to prevent rapid successive queries
  const debouncedAdditionalBody = useDebounce(additionalBody, 500);

  const ebayListingOptions = Object.keys(ListingStatusEnum)
    .filter(key => isNaN(Number(key)))
    .map((key, index) => ({
      id: index,
      name: key,
    }));


  const cols = useMemo(
    () => [
      TableCheckboxCol(true),
      colHelper.display({
        id: "images",
        cell: (info) => <PartImage part={info.row.original} width="min-w-10 max-w-32" />
      }),
      colHelper.accessor("tagNumber", {
        header: "Tag No.",
        sortingFn: "auto",
        cell: (info) => <ClickToCopy text={info.getValue()} />
      }),
      colHelper.accessor(row => row.category?.name, {
        header: "Category",
        id: "categoryId",
        sortingFn: "auto",
        cell: (info) => <span>{info.row.original.category?.name}</span>,
        sortDescFirst: false,
      }),
      colHelper.accessor("vehicle.combinedMake", {
        header: "Make, Model & Reg",
        enableSorting: false,
        cell: (info) => <div>
          <p>{info.row.original.vehicle?.combinedMake}</p>
          <p>{info.row.original.vehicle?.combinedModel}</p>
          <p>{info.row.original.vehicle?.dvlaYearOfManufacture}</p>
          <ClickToCopy text={info.row.original.vehicle?.vrm} />
        </div>,
      }),
      colHelper.accessor(row => {
        const offer = row.offers[0];
        return offer ? ((offer.auctionStartPrice ?? 0) + (offer.buyItNowPrice ?? 0)) : 0;
      }, {
        id: "offer price",
        header: "Price",
        sortingFn: (rowA: any, rowB: any, columnId: any) => {
          //Sort by value of both
          return (parseFloat(rowA.original.offers[0]?.auctionStartPrice ?? 0)) + (parseFloat(rowA.original.offers[0]?.buyItNowPrice ?? 0)) -
            (parseFloat(rowB.original.offers[0]?.auctionStartPrice ?? 0)) - (parseFloat(rowB.original.offers[0]?.buyItNowPrice ?? 0));
        },
        cell: (info) => <UnlistedOfferPrice part={info.row.original} />
      }),
      colHelper.accessor("warehouseLocationId", {
        header: "Warehouse",
        sortingFn: "auto",
        sortDescFirst: false,
        cell: (info) => <WarehouseLocationFromId id={info.getValue()} locatedById={info.row.original.locatedBy} lastStockChecked={info.row.original.lastStockChecked} />,
      }),
      // colHelper.accessor("grade", {
      //   header: "Grade",
      //   sortingFn: "auto",
      // }),
      // colHelper.accessor("vehicle.odometerInMiles", {
      //   header: "Mileage",
      //   sortingFn: "auto",
      // }),
      colHelper.accessor("category", {
        header: "Fitment",
        enableSorting: false,
        cell: (info) => <div>
          <p>{info.getValue()?.mechOnly && "Mech"}</p>
          <p>{info.getValue()?.partOnly && "Body"}</p>
        </div>,
      }),
      colHelper.accessor("offers", {
        id: "listed by",
        header: "Listed By",
        enableSorting: false,
        cell: (info) => {
          const publishedBy = info.getValue()?.at(0)?.publishedBy;
          return publishedBy ? <ContactFromId contactId={publishedBy} /> : <span>Not Set</span>;
        },
      }),
      colHelper.accessor("offers", {
        id: "offer status",
        header: "Ebay Status",
        enableSorting: false,
        cell: (info) => <ListingStatus offer={info.getValue()?.at(0)} part={info.row.original} />,
      }),
      colHelper.accessor("offers", {
        id: "order status",
        header: "Order Status",
        enableSorting: false,
        cell: (info) => <OrderStatus part={info.row.original} />,
      }),
      colHelper.accessor("partStatus", {
        header: "Part Status",
        enableSorting: false,
        cell: (info) => <PartStatusDropdown part={info.row.original} />,
      }),

    ],
    []
  );

  const handleSendToQc = (rows: RowModel<IPart>) => {
    rows.rows.forEach((row) => {
      let part = row.original;

      if (part.partStatus === PartStatus.QC) {
        return;
      }

      patchPart.mutate({ id: part.id, partStatus: PartStatus.QC }, {
        onSuccess: () => {
          saveHandlers.onSuccess();
          queryClient.invalidateQueries(["pagedTable", pageMode]);
        },
        onError: () => {
          saveHandlers.onError();
        }
      });
    });
  };

  const handleMultiPrint = async (rows: RowModel<IPart>) => {
    var parts = new Array();

    rows.rows.forEach((row) => {
      parts.push(row.original);
    });

    let htmlPages = [] as string[];

    // Use Promise.all to wait for all promises to resolve
    await Promise.all(parts.map(async (part) => {
      const html = await generatePartLabel(part);
      htmlPages.push(html);
    }));

    //Stitch together with a page break inbetween each
    const allHtml = htmlPages.join("<div style='page-break-after: always;'></div>");

    var iframe = document?.getElementById("frame") as HTMLIFrameElement;
    var pri = iframe.contentWindow;
    pri?.document.open();
    pri?.document.write(allHtml);
    pri?.document.close();
    pri?.focus();

    //QR code doesn't show up without a timeout
    setTimeout(async () => {
      pri?.print();
    }, 1);

    // table.resetRowSelection();
  };


  const handleDeleteParts = async () => {
    if (!toDelete) return;

    setDeleteLoading(true);

    try {
      await Promise.all(
        toDelete.rows.map(async (row) => {
          await deletePart.mutateAsync(row.original.id);

          // Deselect the row after deletion
          row.toggleSelected(false);
        })
      );

      deleteHandlers.onSuccess();
      setToDelete(undefined);
      setConfirmOpen(false);
      queryClient.invalidateQueries(["pagedTable", pageMode]);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleGetVehicle = () => {
    const vrm = watch("vrm");
    if (!vrm || fetchLoading || isSearching || isSearchDisabled) return;

    // Clear any existing vehicle timeout
    if (vehicleTimeoutRef.current) {
      clearTimeout(vehicleTimeoutRef.current);
    }

    setFetchLoading(true);
    vrmLookup.mutate(vrm, {
      onSuccess: (data) => {

        const vehicle = data.data;
        if (vehicle) {
          setValue("make", (vehicle.combinedMake ?? "").trim() as any);
          setValue("model", (vehicle.modelRangeDescription ?? "").trim() as any);
          setValue("year", vehicle.dvlaYearOfManufacture?.toString() ?? "");

          setValue("bodyStyle", vehicle.bodyStyle);
          setValue("numberOfDoors", vehicle.numberOfDoors?.toString() ?? "");
          setValue("fuelType", vehicle.combinedFuelType);
          setValue("transmissionType", vehicle.combinedTransmission);
          setValue("engineCode", vehicle.engineModelCode);


          var yearFrom = vehicle.visibilityDate && DateTime.fromISO(vehicle.visibilityDate).year;
          var yearTo = vehicle.terminateDate && DateTime.fromISO(vehicle.terminateDate).year;
          setValue("yearFrom", yearFrom?.toString() ?? "");
          setValue("yearTo", yearTo?.toString() ?? "");

          const engineSize = vehicle.engineCapacity
            ? (Math.round(vehicle.engineCapacity / 100) * 100 / 1000).toString()
            : "";
          setValue("engineSize", engineSize);
        }
      },
      onError: () => {
        // addNotification({

        // })
      },
      onSettled: () => {
        // Add a small delay before allowing another vehicle lookup
        vehicleTimeoutRef.current = setTimeout(() => {
          setFetchLoading(false);
        }, 500);
      },
    });
  };

  const handleSmartSearch = () => {
    const data = getValues();

    if (!data.vrm || isSearching || fetchLoading) return;

    setIsSearching(true);
    setIsSmartMatch(true);
    setAdditionalBody({
      smartSearch: data.vrm,
      categoryId: data.partCategory?.id,
      listingStatus: data.listingStatus,
    });

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Reset search flag after a delay to allow the query to start
    timeoutRef.current = setTimeout(() => setIsSearching(false), 1000);
  };

  const handleClearForm = () => {
    reset();
    setIsSmartMatch(false);
    setDates({ startDate: null, endDate: null });
    setDateFieldType("Created");
    setAdditionalBody({});
  };

  const handleStatusUpdate = () => {
    const body = {
      partIds: toUpdate?.rows.map((row) => row.original.id),
      data: {
        partStatus: statusUpdate,
      }
    };

    bulkPatchPart.mutate(body, {
      ...saveHandlers,
      onSuccess: () => {
        saveHandlers.onSuccess();
        setToUpdate(undefined);
        setUpdateStatusOpen(false);
        setStatusUpdate(0);
      },
    });
  };

  const handlePriceUpdate = () => {
    const body = {
      offers: toUpdate?.rows.map((row) => ({
        offerId: row.original.offers[0]?.id,
        partId: row.original.id,
      })),
      price: priceUpdate,
    };

    bulkUpdateOfferPrice.mutate(body, {
      ...saveHandlers,
      onSuccess: () => {
        saveHandlers.onSuccess();
        setToUpdate(undefined);
        setUpdatePriceOpen(false);
        setPriceUpdate(0);
      },
    });
  };

  const handleAddToBasket = (rows: RowModel<IPart>) => {
    let partIds = rows.rows.map((row) => row.original.id);

    if (!partIds || partIds.length === 0) return;

    addItemsMultiple.mutate(partIds);
  };

  //Search button
  const onSubmit = (data: IFormFields) => {
    if (isSearching || fetchLoading) return; // Prevent multiple simultaneous searches

    // Prevent form submission if the component is not fully ready
    if (!data || typeof data !== 'object') return;

    setIsSearching(true);

    let dataCopy: Partial<IFormFields> = {};

    if (!isExtended) {
      //Don't send values we can't see
      dataCopy = {
        ...defaultFormValues,
        make: data.make,
        model: data.model,
        year: data.year,
        partCategory: data.partCategory,
        listingStatus: data.listingStatus,
        locationSearchMode: data.locationSearchMode,
        warehouseLocationId: data.locationSearchMode === "equals" ? data.warehouseLocation?.id : undefined,
        warehouseLocationBeginsWith: data.locationSearchMode === "beginsWith" ? data.warehouseLocationBeginsWith : undefined,
      };
    } else {
      dataCopy = { ...data };
      dataCopy.warehouseLocationId = data.locationSearchMode === "equals" ? data.warehouseLocation?.id : undefined;
      dataCopy.warehouseLocationBeginsWith = data.locationSearchMode === "beginsWith" ? data.warehouseLocationBeginsWith : undefined;
    }

    dataCopy.vrm = undefined;
    dataCopy.smartSearch = undefined;
    setIsSmartMatch(false);

    // Add date range filters
    const bodyWithDates = {
      ...dataCopy,
      startDate: dates.startDate?.toISOString(),
      endDate: dates.endDate?.toISOString(),
      dateFieldType: dateFieldType,
      categoryId: dataCopy.partCategory?.id,
    };

    setAdditionalBody(bodyWithDates);

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Reset search flag after a delay to allow the query to start
    timeoutRef.current = setTimeout(() => setIsSearching(false), 1000);
  };

  const watchPartCategory = watch("partCategory");
  const watchListingStatus = watch("listingStatus");
  const watchWarehouseLocation = watch("warehouseLocation");

  const handleDatesChange = (newDates: DateRangeType) => {
    setDates(newDates);
  };

  const handleMakeSelect = (make: { make?: string } | null) => {
    setValue("make", make?.make ?? "" as any);
    setValue("model", "" as any);
  };

  const watchLocationSearchMode = watch("locationSearchMode");
  const watchWarehouseLocationBeginsWith = watch("warehouseLocationBeginsWith");
  const watchTagNumber = watch("tagNumber");

  useEffect(() => {
    if (!isAdvancedSearch) {
      setAdditionalBody({
        categoryId: watchPartCategory?.id,
        listingStatus: watchListingStatus,
        locationSearchMode: watchLocationSearchMode,
        warehouseLocationId: watchLocationSearchMode === "equals" ? watchWarehouseLocation?.id : undefined,
        warehouseLocationBeginsWith: watchLocationSearchMode === "beginsWith" ? watchWarehouseLocationBeginsWith || undefined : undefined,
        startDate: dates.startDate?.toISOString(),
        endDate: dates.endDate?.toISOString(),
        dateFieldType: dateFieldType,
        tagNumber: watchTagNumber,
      });
    }
  }, [isAdvancedSearch, watchPartCategory, watchListingStatus, watchLocationSearchMode, watchWarehouseLocation, watchWarehouseLocationBeginsWith, dates, dateFieldType, watchTagNumber]);


  // Monitor PagedTable query state to prevent crashes
  const [queryState, setQueryState] = useState<any>(null);

  useEffect(() => {
    // Subscribe to query cache changes and find any PagedTable queries
    const unsubscribe = queryClient.getQueryCache().subscribe(() => {
      const cache = queryClient.getQueryCache();
      const pagedTableQueries = cache.getAll().filter(query =>
        query.queryKey[0] === "pagedTable"
      );

      // Get the most recent PagedTable query state
      const latestQuery = pagedTableQueries[pagedTableQueries.length - 1];
      setQueryState(latestQuery?.state || null);
    });
    return () => unsubscribe();
  }, [queryClient]);

  const isSearchDisabled = useMemo(
    () => !queryState || queryState.status === "loading" || isSearching || fetchLoading,
    [queryState, isSearching, fetchLoading]
  );

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    return () => {
      // Cleanup any pending timeouts
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (vehicleTimeoutRef.current) {
        clearTimeout(vehicleTimeoutRef.current);
      }
    };
  }, []);

  const handleOpenChangeCategory = (rows: RowModel<IPart>) => {
    setToUpdate(rows);
    setUpdateCategoryOpen(true);
  };

  const actionButtons = useMemo(() => [
    { label: "Change Category", onClick: handleOpenChangeCategory, disabledOnNoRows: true },
    { label: "Send to QC", onClick: handleSendToQc, disabledOnNoRows: true },
    { label: "Reprint Labels", onClick: handleMultiPrint, disabledOnNoRows: true },
    {
      label: "Change Part Status",
      disabledOnNoRows: true,
      onClick: (rows: RowModel<IPart>) => {
        setToUpdate(rows);
        setUpdateStatusOpen(true);
      },
    },
    {
      label: "Change Price",
      disabledOnNoRows: true,
      onClick: (rows: RowModel<IPart>) => {
        setToUpdate(rows);
        setUpdatePriceOpen(true);
      },
    },
    {
      label: "Add To Basket",
      disabledOnNoRows: true,
      onClick: handleAddToBasket,
    },
    {
      label: "Delete Parts",
      disabledOnNoRows: true,
      onClick: (rows: RowModel<IPart>) => {
        setToDelete(rows);
        setConfirmOpen(true);
      },
    },
  ], [handleSendToQc, handleMultiPrint, handleAddToBasket]);

  const watchMake = watch("make");
  const watchModel = watch("model");

  return (
    <>
      <ChangeCategoryModal open={updateCategoryOpen} setOpen={setUpdateCategoryOpen} setToUpdate={setToUpdate} parts={toUpdate?.rows.map((row) => row.original)!} />
      <Modal open={updateStatusOpen} setOpen={setUpdateStatusOpen} width="max-w-3xl">
        <div className="space-y-4">
          <p>You are about to update {toUpdate?.rows.length} parts!</p>
          <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-2">
            <select value={statusUpdate} className={`border p-1`} onChange={(e) => setStatusUpdate(parseInt(e.target.value))}>
              {Object.values(PartStatus).filter((ct) => isNaN(Number(ct))).map((status, i) => (
                <option key={status} value={i}>
                  {status}
                </option>
              ))}
            </select>
            <ButtonInput label={"Update All"} classes={""} onClick={handleStatusUpdate} />
          </div>
        </div>
      </Modal>
      <Modal open={updatePriceOpen} setOpen={setUpdatePriceOpen} width="max-w-3xl">
        <div className="space-y-4">
          <p>You are about to update {toUpdate?.rows.length} parts!</p>
          <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-2">
            <div className="flex items-center w-full">
              £ <input
                step={0.01}
                defaultValue={priceUpdate}
                className="h-8 w-full border p-1 text-sm" type="number"
                onChange={(e) => setPriceUpdate(parseFloat(e.target.value))}
              />
            </div>
            <ButtonInput label={"Update All"} classes={""} onClick={handlePriceUpdate} />
          </div>
        </div>
      </Modal>
      <ConfirmModal
        open={confirmOpen}
        setOpen={setConfirmOpen}
        title={"Delete these parts?"}
        message={`Are you sure you want to delete these parts? \n This action cannot be undone.`}
        confirmButtonText={"Delete"}
        onConfirm={handleDeleteParts}
        isLoading={deleteLoading}
      />

      <ButtonInput
        onClick={() => setIsAdvancedSearch(!isAdvancedSearch)}
        label={isAdvancedSearch ? "Normal Search" : "Advanced Search"}
        classes={"mt-4"}
      />

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-y-2 mt-2">
        {isAdvancedSearch && (
          <>
            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
              <TextInput
                label={"Get Vehicle Details"}
                placeholder="Enter VRM or VIN"
                register={register}
                registerName={"vrm"}
                disabled={fetchLoading || isSearching}
              />
              <ButtonInput
                label={fetchLoading ? "Fetching..." : "Fetch Details"}
                onClick={handleGetVehicle}
                classes={"mt-6"}
                isLoading={fetchLoading}
                disabled={isSearchDisabled || fetchLoading}
              />
              <ButtonInput
                label="Smart Match"
                onClick={handleSmartSearch}
                classes={classNames(isSearchDisabled || fetchLoading ? "" : "!bg-[#00909c] hover:!bg-[#00b3c2]", "mt-6")}
                disabled={isSearchDisabled || fetchLoading}
                isLoading={isSearching}
              />
              <PopOutBox classes="w-96">
                <p>Smart Match will check for parts that fit your vehicle based on key details like make, model, year, and other specifications.</p>
                <p>It finds body parts that match your car's exterior and mechanical parts that fit your engine and transmission.</p>
              </PopOutBox>
            </div>

            <div className={classNames(isSmartMatch ? "[&>div>input]:!bg-gray-200" : "", "grid grid-cols-1 lg:grid-cols-5 gap-4")}>
              <MakeCombobox label="Make" onMakeSelect={handleMakeSelect} currentValue={watchMake} />
              <ModelCombobox label="Model" make={watchMake} onModelSelect={(model) => setValue("model", (model?.range ?? "") as any)} currentValue={watchModel} />
              <TextInput label={"Year"} register={register} registerName={"year"} />

              <div className="lg:col-span-2"></div>
            </div>

            {isSmartMatch && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mt-2">
                <p className="text-blue-800 text-sm">
                  <strong>Smart Match Active:</strong> Searching for parts that match your vehicle specifications.
                  Vehicle details are locked and cannot be modified while smart match is active.
                </p>
              </div>
            )}

            <div>
              <ButtonInput
                label={isExtended ? "Use Fewer" : "Use More Filters"}
                onClick={() => setIsExtended(!isExtended)}
                classes={"!px-2 !py-1 !text-xs"}
                disabled={isSearchDisabled}
              />
            </div>

            <div
              className={classNames(
                isSmartMatch ? "[&>div>input]:!bg-gray-200" : "",
                isExtended ? "max-h-screen opacity-100" : "max-h-0 opacity-0 overflow-hidden",
                "grid grid-cols-1 lg:grid-cols-5 gap-4 transition-all duration-300"
              )}
            >
              <TextInput label={"Body Style"} register={register} registerName={"bodyStyle"} />
              <TextInput label={"Number of Doors"} register={register} registerName={"numberOfDoors"} />
              <TextInput label={"Fuel Type"} register={register} registerName={"fuelType"} />

              <div className="lg:col-span-2"></div>

              <TextInput label={"Engine Size"} register={register} registerName={"engineSize"} />
              <TextInput label={"Engine Code"} register={register} registerName={"engineCode"} />
              <TextInput label={"Transmission Type"} register={register} registerName={"transmissionType"} />

              <div className="lg:col-span-2"></div>

              <TextInput label={"Year From"} register={register} registerName={"yearFrom"} />
              <TextInput label={"Year To"} register={register} registerName={"yearTo"} />
              <TextInput label={"Part Number"} register={register} registerName={"partNumber"} />
            </div>

            <hr className="mt-2" />

            <div className="flex items-center gap-x-4 mt-2">
              <ButtonInput
                label={isSearching ? "Searching..." : "Search"}
                isSubmit
                classes={""}
                onClick={undefined}
                disabled={isSearchDisabled}
                isLoading={isSearching}
              />
              <ButtonInput
                label={"Clear"}
                classes={classNames(isSearchDisabled ? "" : `!bg-red-600 hover:!bg-red-700`)}
                onClick={handleClearForm}
                disabled={isSearchDisabled}
              />
            </div>

          </>
        )}

        <div className="flex flex-wrap gap-x-6 gap-y-2 items-center">
          <div className="flex">
            <ComboBoxInput
              label={"Part Category"}
              register={register}
              registerName={"partCategory"}
              options={allCategories.data?.data ?? []}
              setValue={setValue}
              disabled={isSearchDisabled || allCategories.isLoading}
              searchField={"name"}
              classes="min-w-96"
              placeholder="Start Typing to Search For a Category"
            />
          </div>

          <div className="flex w-full max-w-64">
            <SelectInput
              label={"eBay Status"}
              register={register}
              registerName={"listingStatus"}
              options={ebayListingOptions}
              setValue={setValue}
              watch={watch}
              cols="w-full"
              disabled={isSearchDisabled}
            />
          </div>

          <WarehouseLocationSearch
            showSearchOptions={true}
            register={register}
            setValue={setValue}
            watch={watch}
            isSearchDisabled={isSearchDisabled}
            warehouseLocations={warehouseLocations}
          />

          <DateRangeFilter
            dates={dates}
            onDatesChange={handleDatesChange}
            dateFieldType={dateFieldType}
            onDateFieldTypeChange={setDateFieldType}
            disabled={isSearchDisabled}
          />

          <TextInput
            label="Tag Number"
            register={register}
            registerName={"tagNumber"}
            placeholder="Tag Number Only"
          />
        </div>
      </form>


      <PagedTable
        key={isAdvancedSearch ? advancedKey : genericKey}
        url={isAdvancedSearch ? advancedUrl : genericUrl}

        columns={cols}
        pageMode={pageMode}
        queryLabel={isAdvancedSearch ? undefined : "Search stock by Tag, Part No., Make, Model, Engine Code, Category, or Warehouse"}
        onRowClick={(x: IPart) => openInNewTab(`stock/${x.id}`)}
        actionButtons={showActions ? actionButtons : []}
        showTotal={true}
        additionalBody={debouncedAdditionalBody}
        expandedRow={(part) => (
          <div className="flex items-center gap-x-2 divide-x p-2 [&>div]:px-2">
            <div className="flex flex-col items-center gap-y-2">
              <p>Grade:</p>
              {part.grade ? <span className="text-sm">{part.grade}</span> : "Not Set"}
            </div>
            <div className="flex flex-col items-center gap-y-2">
              <p>Mileage:</p>
              <span className="text-sm">{part.vehicle?.odometerInMiles ?? "Not Set"}</span>
            </div>
            <div className="flex flex-col items-center gap-y-2">
              <p>Part Number:</p>
              <UnlistedNpmUpdate part={part} />
            </div>
            <div className="flex flex-col items-center gap-y-2">
              <p>Dismantled By:</p>
              {part.removedBy ? <ContactFromId contactId={part.removedBy} /> : "Not Set"}
            </div>
            <div className="flex flex-col items-center gap-y-2">
              <ButtonInput
                label="View on eBay"
                isSubmit={false}
                classes={undefined}
                onClick={() => openInNewTab(`https://www.ebay.co.uk/itm/${part.ebayItemNumber}`)}
              />
              <ButtonInput
                label="View Vehicle"
                isSubmit={false}
                onClick={() => openInNewTab("/vehicles/" + part.vehicle?.id)}
                classes={undefined}
              />
            </div>
            {/* <UnlistedOfferPrice part={part} /> */}
          </div>
        )}
      />

      <iframe id="frame" className="h-0 w-0 absolute"></iframe>
    </>
  );
}
